import React, { ReactNode, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Globe } from 'lucide-react';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { explainerSlugKey } from 'src/shared/constants/portfolio';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import usePortfolioAnalytics from 'src/app/portfolio/views/portfolio-summary/hooks/usePortfolioAnalytics';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType } from 'src/shared/types/graph/survey';
import { baselineYearFor, ComparisonPeriod, coversDifferentAssets, landCoverComparison } from 'src/shared/helpers/habitatComparison';
import TrendPill, { percentageTrendExample, trendColour } from 'src/shared/components/trend-pill/TrendPill';
import Show from 'src/shared/components/show/Show';
import usePreviewRelease from 'src/shared/hooks/usePreviewRelease';
import ComparisonPeriodToggle from 'src/shared/components/comparison-period-toggle/ComparisonPeriodToggle';
import { BaselineFallback, baselineFallback } from 'src/shared/helpers/baselineFallback';
import PortfolioExploreButton from './PortfolioExploreButton';
import SummaryCard from './SummaryCard';

const landCoverChartColors: { label: string; color: string; metricKey?: string }[] = [
    { label: 'Water', color: '#6B98E1' },
    { label: 'Trees', color: '#4C7F47' },
    { label: 'Grass', color: '#8EB24E' },
    { label: 'Flooded vegetation', metricKey: 'Flooded Vegetation', color: '#8385C8' },
    { label: 'Crops', color: '#D19833' },
    { label: 'Shrub and scrub', metricKey: 'Shrub and Scrub', color: '#D4C556' },
    { label: 'Built', color: '#AD2A21' },
    { label: 'Bare', color: '#A29B8F' },
    { label: 'Snow and ice', metricKey: 'Snow and Ice', color: '#B39DE3' },
    { label: 'Cumulative vegetation area', color: '#4C7F47', metricKey: 'Cumulative Vegetation Area' },
];

const LandCoverChart = ({ portfolioData, chosenBaseline }: { portfolioData: PortfolioData; chosenBaseline?: number }) => {
    const landCover = portfolioData.summary.metrics.find(metric => metric.label === 'Land Cover');
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();

    const trackAnalytics = usePortfolioAnalytics();
    const [period, setPeriod] = useState<ComparisonPeriod>('current');
    const showHabitatComparison = usePreviewRelease('habitatComparison');

    if (!landCover || !landCover.metricWithTrend || Object.keys(landCover.metricWithTrend).length === 0) {
        return null;
    }

    const { years, coverage, comparisonYear, shownPeriod, hasLastFullYear } = landCoverComparison(landCover, period);
    const comparisonMetrics = landCover.metricWithTrend[comparisonYear];
    const comparisonCoverage = coverage.find(entry => entry.year === comparisonYear);

    const baselineYear = baselineYearFor(years, comparisonYear, chosenBaseline);
    const fallback = baselineFallback(years, chosenBaseline, baselineYear);
    const baselineMetrics = landCover.metricWithTrend[baselineYear];
    const coversDifferentSites = coversDifferentAssets(coverage, baselineYear, comparisonYear);

    const calculateTrend = (metricKey: string) => {
        const baselineValue = baselineMetrics[metricKey] || 0;
        const comparisonValue = comparisonMetrics[metricKey] || 0;
        return ((comparisonValue - baselineValue) / (baselineValue || 1)) * 100;
    };

    const vegetationAreaTrend = calculateTrend('Cumulative Vegetation Area');
    const treesTrend = calculateTrend('Trees');

    const pieChartData = landCoverChartColors
        .filter(entry => entry.label !== 'Cumulative vegetation area')
        .map(entry => ({
            label: entry.label,
            value: comparisonMetrics[entry.metricKey || entry.label] || (0 as number),
            color: entry.color,
            baselineYear,
            comparisonYear,
            trendValue: calculateTrend(entry.metricKey || entry.label),
        }));

    const handleExploreClick = () => {
        navigate.toPortfolioSiteExplorer({
            customerId: currentCustomerId,
            selectedMetric: HabitatInsightsMetricType.LAND_COVER,
            sortDirection: SortOrder.ASC,
            view: 'list',
        });
    };

    return (
        <SummaryCard
            dataTestId='portfolio-land-cover-chart'
            containerClasses='gap-3 p-5'
            cardIcon={<Globe className='text-primary' />}
            cardTitle='Land cover'
            cardSubtitle='Distribution of land cover types across the site, highlighting dominant land cover and land uses.'
            titleExplainerSlug={{
                defaultSlug: explainerSlugKey.portfolioLandCover,
                explainerSlug: `${explainerSlugKey.portfolioLandCover}-te`,
            }}
            cardBody={
                <div className='flex gap-5'>
                    <div className='flex gap-6'>
                        <CountBlock
                            count={`${comparisonMetrics['Cumulative Vegetation Area'] || 0}%`}
                            metricName='Combined vegetated area'
                            text={
                                <div className='flex gap-2 items-center'>
                                    Combined vegetated area
                                    <div className='mt-[-5px]'>
                                        <QuestionButtonHelp
                                            type='api'
                                            placement='right'
                                            slug={explainerSlugKey.portfolioCombinedVegetatedArea}
                                            detailedExplainerSlug={`${explainerSlugKey.portfolioCombinedVegetatedArea}-te`}
                                            onTooltipHover={() =>
                                                trackAnalytics.trackExplainerHover({
                                                    slug: explainerSlugKey.portfolioCombinedVegetatedArea,
                                                })
                                            }
                                            onDetailedHelpClick={() =>
                                                trackAnalytics.trackDetailedExplainerClick({
                                                    slug: `${explainerSlugKey.portfolioCombinedVegetatedArea}-te`,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            }
                            baselineYear={baselineYear}
                            comparisonYear={comparisonYear}
                            fallback={fallback}
                            trendValue={vegetationAreaTrend}
                        />

                        <CountBlock
                            count={`${comparisonMetrics.Trees || 0}%`}
                            metricName='Tree cover'
                            text='Tree Cover'
                            baselineYear={baselineYear}
                            comparisonYear={comparisonYear}
                            fallback={fallback}
                            trendValue={treesTrend}
                        />
                    </div>

                    <div className='bg-grey-20 w-[1px]'>&nbsp;</div>

                    <div className='flex gap-8'>
                        <div className='relative h-[140px] w-[140px]'>
                            <div
                                className='absolute inset-0 flex flex-col items-center justify-center text-grey-80'
                                data-testid='land-cover-comparison-year'
                            >
                                <span className='text-xl'>{comparisonYear}</span>
                                {comparisonCoverage && (
                                    <span className='text-xs'>{comparisonCoverage.complete ? 'Full year' : 'Year to date'}</span>
                                )}
                            </div>
                            <ResponsiveContainer width='100%' height='100%'>
                                <PieChart>
                                    <Pie
                                        dataKey='value'
                                        data={pieChartData}
                                        cx='50%'
                                        cy='50%'
                                        innerRadius={45}
                                        outerRadius={65}
                                        fill='#8884d8'
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>

                                    <Tooltip content={<CustomTooltip />} allowEscapeViewBox={{ x: true, y: true }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className='flex gap-5'>
                            <div className='flex flex-col gap-2'>
                                {pieChartData.slice(0, 5).map((entry, index) => (
                                    <div key={index} className='flex gap-2 items-center'>
                                        <span className='rounded-full h-4 w-4' style={{ backgroundColor: entry.color }}></span>
                                        <div className='flex gap-3'>
                                            <span className='text-sm text-grey-80 w-[140px]'>{entry.label}</span>
                                            <span className='text-sm text-black min-w-[4ch] text-right tabular-nums'>{entry.value}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className='flex flex-col gap-2 place-self-start'>
                                {pieChartData.slice(5).map((entry, index) => (
                                    <div key={index} className='flex gap-2 items-center'>
                                        <span className='rounded-full h-4 w-4' style={{ backgroundColor: entry.color }}></span>
                                        <div className='flex gap-3'>
                                            <span className='text-sm text-grey-80 w-[140px]'>{entry.label}</span>
                                            <span className='text-sm text-black min-w-[4ch] text-right tabular-nums'>{entry.value}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            }
            action={
                <div className='flex flex-col gap-1 items-end'>
                    <div className='flex gap-3 items-center'>
                        <Show when={showHabitatComparison && hasLastFullYear}>
                            <ComparisonPeriodToggle
                                value={shownPeriod}
                                onChange={value => {
                                    setPeriod(value);
                                    trackAnalytics.trackLandCoverPeriodChange(value);
                                }}
                            />
                        </Show>
                        <PortfolioExploreButton sectionName='land-cover' onClick={handleExploreClick} />
                    </div>
                    {coversDifferentSites && (
                        <div role='status' className='text-xs text-orange-100'>
                            Sites differ between {baselineYear} and {comparisonYear}
                        </div>
                    )}
                </div>
            }
            frameworks={landCover.frameworks}
        />
    );
};

type CountBlockProps = {
    count: number | string;
    // The metric as the trend explanation names it.
    metricName: string;
    text: ReactNode;
    baselineYear: number;
    comparisonYear: number;
    trendValue: number;
    fallback?: BaselineFallback;
};

const CountBlock = ({ count, metricName, text, baselineYear, comparisonYear, trendValue, fallback }: CountBlockProps) => (
    <div className='flex flex-col gap-3'>
        <div className='flex flex-col'>
            <span className='text-[42px] text-grey-80 tabular-nums'>{count}</span>
            <span className='flex items-center text-sm text-grey-80 pl-1'>{text}</span>
        </div>
        <TrendPill
            trendValue={trendValue}
            baselineYear={baselineYear}
            comparisonYear={comparisonYear}
            example={percentageTrendExample(metricName)}
            fallback={fallback}
        />
    </div>
);

// Used type any as it is expected by Recharts Tooltip
const CustomTooltip = ({ active, payload }: any) => {
    const isVisible = active && payload && payload.length;
    if (!isVisible) {
        return null;
    }

    const payloadMetadata = payload[0].payload;
    const colorInfo = trendColour(payloadMetadata.trendValue);

    return (
        <div className='flex-1 flex self-stretch flex-col gap-3 bg-white p-4 rounded-lg shadow-portfolio-summary-card min-w-[240px]'>
            <div className='flex gap-2 items-center'>
                <span className='rounded-full h-4 w-4' style={{ backgroundColor: payload[0].payload.color }}></span>
                <span className='text-sm text-black'>{payloadMetadata.label}</span>
            </div>
            <div className='flex flex-col gap-2'>
                <div className='flex gap-2 justify-between'>
                    <span className='text-sm text-grey-80'>{payloadMetadata.comparisonYear} Land cover:</span>
                    <span className='flex text-xs text-black'>{payloadMetadata.value}%</span>
                </div>
                <div className='flex gap-2 justify-between'>
                    <span className='text-sm text-grey-80'>Trend since {payloadMetadata.baselineYear}:</span>
                    <span className={`flex text-xs items-center gap-1 ${colorInfo.colorClass}`}>
                        {colorInfo.arrowIcon}
                        {payloadMetadata.trendValue.toFixed(2)}%
                    </span>
                </div>
            </div>
        </div>
    );
};

export default LandCoverChart;
